import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  NORDIC_REVIEW_REPO,
  NORDIC_REVIEW_TARGET_HOURS,
  type INordicReviewMetricResponse,
  type NordicReviewClock,
} from '@skystone/shared';
import { Spinner, ErrorBanner } from '@/components/ui';
import { cn } from '@/lib/utils';
import { nordicReviewApi } from '@/services/nordic-review-api';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { NordicReviewBarChart } from './NordicReviewBarChart';
import { NordicReviewMethodology } from './NordicReviewMethodology';
import { NordicReviewTeamCards } from './NordicReviewTeamCards';

// ---------------------------------------------------------------------------
// Nordic review flow (PRODOPSAI-678)
//
// Rendered on the Engineering (DORA) tab, for the Nordic ERP product only.
//
// Unlike the four DORA cards above it, these charts genuinely filter by team.
// The tab's existing caveat says team selection narrows to the product context,
// which stays true for those cards — hence the banner here saying the opposite
// for this section, so one dropdown change moving only half the page is
// explained rather than discovered.
// ---------------------------------------------------------------------------

const CLOCK_PARAM = 'clock';
const DEFAULT_CLOCK: NordicReviewClock = 'office';

const CLOCK_OPTIONS: ReadonlyArray<{ value: NordicReviewClock; label: string }> = [
  { value: 'office', label: 'Office hours' },
  { value: 'calendar', label: 'Calendar hours' },
];

function parseClock(value: string | null): NordicReviewClock {
  return value === 'calendar' ? 'calendar' : DEFAULT_CLOCK;
}

const MS_PER_DAY = 86_400_000;
/**
 * Longest span still bucketed weekly, in days (~12 weeks).
 *
 * A grouped bar chart carries three bars per column, so it runs out of room far
 * sooner than the single-series line charts the page's pills were sized for.
 * Daily buckets turn a 4-week range into ~28 columns of slivers; weekly buckets
 * turn YTD into ~37. Past this span the chart switches to months, which is also
 * how the teams read their own board.
 */
const MAX_WEEKLY_SPAN_DAYS = 84;

/**
 * Bucket size for the charts, chosen from the span rather than the page's
 * granularity. Floored at weekly, widened to monthly for long ranges.
 */
export function chartGranularityFor(from: string, to: string): 'weekly' | 'monthly' {
  const spanDays = (Date.parse(to) - Date.parse(from)) / MS_PER_DAY;
  if (Number.isNaN(spanDays)) {
    return 'weekly';
  }
  return spanDays > MAX_WEEKLY_SPAN_DAYS ? 'monthly' : 'weekly';
}

interface INordicReviewSectionProps {
  filters: IMetricViewFilters;
  /** Resolved from the scope dropdown; null when "All Teams" is selected. */
  selectedTeamName: string | null;
}

export function NordicReviewSection({
  filters,
  selectedTeamName,
}: Readonly<INordicReviewSectionProps>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const clock = parseClock(searchParams.get(CLOCK_PARAM));

  const [firstReview, setFirstReview] = useState<INordicReviewMetricResponse | null>(null);
  const [merge, setMerge] = useState<INordicReviewMetricResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const setClock = useCallback(
    (next: NordicReviewClock) => {
      const params = new URLSearchParams(searchParams);
      if (next === DEFAULT_CLOCK) {
        params.delete(CLOCK_PARAM);
      } else {
        params.set(CLOCK_PARAM, next);
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const query = useMemo(
    () => ({
      from: filters.from,
      to: filters.to,
      clock,
      granularity: chartGranularityFor(filters.from, filters.to),
      teamName: selectedTeamName ?? undefined,
    }),
    [filters.from, filters.to, clock, selectedTeamName],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([nordicReviewApi.getFirstReview(query), nordicReviewApi.getMerge(query)])
      .then(([firstReviewData, mergeData]) => {
        if (cancelled) return;
        setFirstReview(firstReviewData);
        setMerge(mergeData);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Clear the previous responses too. Leaving them would render the last
        // successful period's bars under an error banner, which reads as live
        // data for the range the user actually selected.
        setFirstReview(null);
        setMerge(null);
        setError(err instanceof Error ? err.message : 'Failed to load review flow metrics');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  const teamNames = useMemo(() => {
    const names = new Set<string>();
    for (const bucket of firstReview?.buckets ?? []) names.add(bucket.teamName);
    for (const bucket of merge?.buckets ?? []) names.add(bucket.teamName);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [firstReview, merge]);

  // Targets are calibrated against the office-hours clock. On calendar they are
  // unreachable, so the line is hidden rather than shown as permanent failure.
  const showTargets = clock === 'office';

  return (
    <section className="space-y-3 border-t border-border pt-4" data-testid="nordic-review-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">
              Review flow — measured per team
            </h3>
            <span className="rounded border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning">
              Nordic ERP only · separate data source
            </span>
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            <span className="font-medium text-foreground">
              This data is not part of Skystone&apos;s normal pipeline.
            </span>{' '}
            It does not come from the warehouse that feeds every other metric on this page —
            review timestamps do not exist there. It is pulled directly from the GitHub API for{' '}
            <code>{NORDIC_REVIEW_REPO}</code>, and covers only the three Nordic teams. No other
            product, repository or team has these figures.
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Unlike the DORA metrics above, these two charts are attributed to the team of the
            person who opened each pull request. Both clocks start when the PR becomes
            reviewable — when a draft is marked <em>Ready for review</em>, or straight away for a
            PR that was never a draft. Automated reviews are not counted.
          </p>
        </div>

        <div
          className="inline-flex shrink-0 rounded-md border border-border p-0.5"
          role="group"
          aria-label="Clock"
        >
          {CLOCK_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setClock(option.value)}
              aria-pressed={clock === option.value}
              className={cn(
                'rounded px-2.5 py-1 text-[11px] font-medium transition-colors',
                clock === option.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className="rounded-lg border border-border p-3">
            <h4 className="mb-2 text-xs font-semibold text-foreground">Time to First Review</h4>
            <NordicReviewBarChart
              buckets={firstReview?.buckets ?? []}
              granularity={query.granularity}
              teamNames={teamNames}
              targetHours={showTargets ? NORDIC_REVIEW_TARGET_HOURS.firstReview : null}
            />
          </div>
          <div className="rounded-lg border border-border p-3">
            <h4 className="mb-2 text-xs font-semibold text-foreground">Time to Merge</h4>
            <NordicReviewBarChart
              buckets={merge?.buckets ?? []}
              granularity={query.granularity}
              teamNames={teamNames}
              targetHours={showTargets ? NORDIC_REVIEW_TARGET_HOURS.merge : null}
            />
          </div>
        </div>
      )}

      <NordicReviewMethodology />

      <NordicReviewTeamCards
        coverage={merge?.coverage ?? []}
        totalPrCount={merge?.totalPrCount ?? 0}
      />
    </section>
  );
}
